import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError, LOGO_URL } from "@/lib/pfc";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp] = useState("");
  const [need2fa, setNeed2fa] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, need2fa ? totp : undefined);
      toast.success("Welcome back");
      nav(loc.state?.from || "/dashboard");
    } catch (err) {
      const detail = formatApiError(err.response?.data?.detail) || err.message;
      if (err.response?.status === 401 && detail === "2FA required") {
        setNeed2fa(true);
        toast.info("Enter your 2FA code");
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inp = "w-full bg-ink-700 border border-white/10 text-white px-4 py-3 font-body focus:border-pfc-gold focus:outline-none focus:ring-1 focus:ring-pfc-gold";

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1574022597485-46926298c9f9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxoaXAlMjBob3AlMjBhcnRpc3QlMjBwZXJmb3JtYW5jZXxlbnwwfHx8fDE3ODEyMjcwNTF8MA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/30 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="label text-pfc-gold mb-2">Public Figures Campaign</div>
          <p className="font-display text-5xl text-white uppercase leading-[0.9]">
            Where Public<br /> Figures, Creators<br /> & Visionaries<br /> <span className="gold-text">Get Seen.</span>
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <img src={LOGO_URL} alt="PFC" className="h-16 w-16 mb-6 object-contain" />
          <h1 className="font-display text-3xl lg:text-4xl text-white uppercase">Sign <span className="gold-text">In</span></h1>
          <p className="text-white/60 mt-2 font-body">Welcome back. Pick up where you left off.</p>

          <form onSubmit={submit} className="space-y-4 mt-8" data-testid="login-form">
            <div>
              <label className="label text-white/70 mb-2 block">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inp} data-testid="login-email" />
            </div>
            <div>
              <label className="label text-white/70 mb-2 block">Password</label>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inp} data-testid="login-password" />
            </div>
            {need2fa && (
              <div data-testid="login-2fa-block">
                <label className="label text-white/70 mb-2 block">2FA Code</label>
                <input
                  required
                  value={totp}
                  onChange={(e) => setTotp(e.target.value)}
                  placeholder="000000"
                  maxLength={6}
                  className={`${inp} text-center tracking-[0.5em] text-lg`}
                  data-testid="login-totp"
                />
                <p className="text-xs text-white/40 mt-1">Open your authenticator app and enter the 6-digit code.</p>
              </div>
            )}
            <button type="submit" disabled={loading} className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 bg-pfc-gold text-ink-900 hover:bg-pfc-gold-bright transition disabled:opacity-50" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="label text-white/40">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button onClick={startGoogle} className="w-full font-sub uppercase tracking-[0.25em] text-sm py-4 border border-white/20 text-white hover:border-pfc-gold hover:text-pfc-gold transition" data-testid="login-google">
            Continue with Google
          </button>

          <p className="text-white/60 mt-6 font-body text-sm text-center">
            New here? <Link to="/register" className="text-pfc-gold hover:underline" data-testid="login-to-register">Create an account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
